import { FormControl, FormLabel, FormControlLabel, FormHelperText } from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import makeStyles from '@mui/styles/makeStyles';
import CSS from 'csstype';

/**
 * Interface for data prop
*/
interface CcfRadioGroupData {
  id: string;
  value: string;
  label: string;
  isDisabled?: boolean;
}

/**
 * Interface for styles prop
 * CSS.Properties provides type checking for CSS properties and values in React 
 * and it can be any valid css properties allowed in React.
*/
interface CcfRadioGroupStyles {
  fieldset?: CSS.Properties;
  legend?: CSS.Properties;
  radioGroup?: CSS.Properties;
  radio?: CSS.Properties;
  formcontrolLabel?: CSS.Properties;
  formHelperText?: CSS.Properties;
}

/**
 * Interface for defining props of CcfRadioGroup component
*/
export interface CcfRadioGroupProps {
  data: Array<CcfRadioGroupData>;
  defaultValue?: string;
  isStandaloneRadio?: boolean;
  name: string;
  label?:boolean;
  error?: boolean;
  helperText?: string;
  horizontalAlign?: boolean;
  labelPlacement?: 'top' | 'start' | 'bottom';
  selected?: string;
  size?: 'small' | 'medium' | undefined;
  styles?: CcfRadioGroupStyles;
  color?: 'secondary' | 'success' | 'default';
  onRadioButtonSelection?: any;
}

/**
 * Using css styles received in styles props
*/
const useStyles = makeStyles({
  fieldset: (styles: any) => styles.fieldset,
  legend: (styles: any) => styles.legend,
  radioGroup: (styles: any) => styles.radioGroup,
  radio: (styles: any) => styles.radio,
  formcontrolLabel: (styles: any) => styles.formcontrolLabel,
  formHelperText: (styles: any) => styles.formHelperText,
});


/**
 * Component used to display Radio Group
 * @param props - CcfRadioGroupProps
 * @example <CcfRadioGroup />
 */

/**
 * @example - CcfRadioGroup - using to show the list of radio buttons in story book 
*/
export function CcfRadioGroup(props: CcfRadioGroupProps) {
  const {
    data,
    defaultValue,
    error,
    helperText,
    horizontalAlign,
    labelPlacement,
    isStandaloneRadio,
    name,
    onRadioButtonSelection,
    selected,
    styles,
    size,
    label,
  } = props;
  
  const ccfRadioStyles =  useStyles(styles);

  return isStandaloneRadio ?
    <>
      {
        data.map((option: CcfRadioGroupData) => <Radio
          className={ccfRadioStyles?.radio}
          checked={selected === option.value}
          key={option.id}
          onChange={onRadioButtonSelection}
          value={option.value}
          name={name}
          inputProps={{
            'aria-label': option.label,
          }}
        />)
      }
    </>
    :
    <FormControl component="fieldset" error={error} className={ccfRadioStyles?.fieldset}>
      <FormLabel component="legend" className={ccfRadioStyles?.legend}>{label}</FormLabel>
      <RadioGroup
        aria-label={name}
        className={ccfRadioStyles?.radioGroup}
        defaultValue={defaultValue}
        value={selected}
        name={name}
        onChange={onRadioButtonSelection}
        row={horizontalAlign}
      >
        {
          data.map((option: CcfRadioGroupData) => (
            <FormControlLabel
              value={option.value}
              control={
                <Radio
                  inputProps={{
                    'aria-label': option.label,
                  }} 
                  className={ccfRadioStyles?.radio}
                  size={size}
                />
              }
              className={ccfRadioStyles?.formcontrolLabel}
              key={option.id}
              labelPlacement={labelPlacement}
              label={option.label}
              disabled={!!option.isDisabled}
            />
          ))
        }
      </RadioGroup>
      { 
      //show helper text when error is set to true
        error && <FormHelperText className={ccfRadioStyles?.formHelperText}>{helperText}</FormHelperText>
      }
    </FormControl>;
}

export default CcfRadioGroup;
